// components/formio/FormioProvider.js
import React, { createContext, useContext, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Formio } from '@formio/js';
// Import Bootstrap here - it will load before any Form.io components
import * as bootstrap from 'bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@formio/js/dist/formio.full.min.css';
// Recolors Form.io's rendered UI to match this app's theme - scoped to
// .formio-theme-scope so it never leaks into MUI or affects anything else.
import './formioTheme.css';

// localStorage key the previous browser-side Form.io login wrote. Still exported so useAuth's
// logout() clears it, and cleared below on mount, so a token left by an older version of the app
// doesn't stay in the browser.
export const FORMIO_TOKEN_BASE_URL_KEY = 'formioTokenBaseUrl';

// Form.io's URL (not a secret): from the runtime config.js (window.APP_CONFIG, mounted from the
// ConfigMap, so it can change without a rebuild), else the build-time variable (local development).
const getFormioBaseUrl = () => window.APP_CONFIG?.FORMIO_BASE_URL || process.env.REACT_APP_FORMIO_BASE_URL;

const FormioContext = createContext();

export const useFormio = () => {
  const context = useContext(FormioContext);
  if (!context) {
    throw new Error('useFormio must be used within FormioProvider');
  }
  return context;
};

// The browser never logs in to Form.io: every form call goes through the backend
// (api/FormioForms), which talks to Form.io with its own service account. So Form.io is "ready"
// as soon as the user is logged in to Keycloak.
export const FormioProvider = ({ children }) => {
  const keycloakAuth = useAuth();

  // Configure Formio globally when provider mounts
  useEffect(() => {

    // Expose Bootstrap globally
    window.bootstrap = bootstrap;

    const baseUrl = getFormioBaseUrl();

    Formio.setBaseUrl(baseUrl);
    Formio.setProjectUrl(baseUrl);
    Formio.setApiUrl(baseUrl);

    // Drop any Form.io admin token an older version of the app stored in this browser.
    Formio.setToken(null);
    localStorage.removeItem(FORMIO_TOKEN_BASE_URL_KEY);

    console.log('Bootstrap initialized globally');
    console.log('Formio configured for self-hosted mode at:', baseUrl);
  }, []);

  const value = {
    isFormioReady: Boolean(keycloakAuth.isAuthenticated),
    keycloakAuth
  };

  return (
    <FormioContext.Provider value={value}>
      {children}
    </FormioContext.Provider>
  );
};
